import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaClock } from 'react-icons/fa';
import { FONTS, getSectionColors } from '../constants/theme';
import {
  EVENT_TYPE_CONFIGS,
  resolveEventConfig,
} from '../constants/calendarConfig';

// Google Calendar API 形式のイベント定義
interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: {
    date?: string; // 終日イベント用 "YYYY-MM-DD"
    dateTime?: string; // 時間指定用 ISO 8601
  };
  end: {
    date?: string;
    dateTime?: string;
  };
  extendedProperties?: {
    private?: {
      type?: string;
      timeLabel?: string;
    };
  };
}

// 曜日の日本語定義
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

const CalendarPage: React.FC = () => {
  // 初期表示は現在の日付（今日）
  const [currentYear, setCurrentYear] = useState(() =>
    new Date().getFullYear(),
  );
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth()); // 0-indexed
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

  const heroColors = getSectionColors('dark');
  const calendarColors = getSectionColors('light');

  // Google Calendar 連携用の State
  const [events, setEvents] = useState<GoogleCalendarEvent[]>([]);

  // Google Calendar API の取得
  useEffect(() => {
    const apiKey =
      import.meta.env.VITE_GOOGLE_CALENDAR_API_KEY ||
      import.meta.env.VITE_GOOGLE_API_KEY;
    const calendarId =
      import.meta.env.VITE_GOOGLE_CALENDAR_ID || 'lodgemilk1982@gmail.com';

    if (!apiKey) {
      setEvents([]);
      return;
    }

    // 表示中の月とその前後の範囲（ISO文字列）
    const timeMin = new Date(
      currentYear,
      currentMonth - 1,
      1,
      0,
      0,
      0,
    ).toISOString();
    const timeMax = new Date(
      currentYear,
      currentMonth + 2,
      0,
      23,
      59,
      59,
    ).toISOString();

    const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
      calendarId,
    )}/events?key=${apiKey}&singleEvents=true&orderBy=startTime&timeMin=${encodeURIComponent(
      timeMin,
    )}&timeMax=${encodeURIComponent(timeMax)}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Google Calendar API Error (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.items) {
          setEvents(data.items);
        } else {
          setEvents([]);
        }
      })
      .catch((err) => {
        console.warn('Google Calendar fetch error:', err);
        setEvents([]);
      });
  }, [currentYear, currentMonth]);

  // 月切り替え処理
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prev) => prev - 1);
    } else {
      setCurrentMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prev) => prev + 1);
    } else {
      setCurrentMonth((prev) => prev + 1);
    }
  };

  // カレンダーの日付リストを算出
  const calendarDays = useMemo(() => {
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();

    const days = [];

    // 前月の余白
    const prevMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevMonthDays = new Date(prevMonthYear, prevMonth + 1, 0).getDate();

    for (let i = firstDayIndex - 1; i >= 0; i--) {
      days.push({
        date: new Date(prevMonthYear, prevMonth, prevMonthDays - i),
        isCurrentMonth: false,
      });
    }

    // 当月の日付
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(currentYear, currentMonth, i),
        isCurrentMonth: true,
      });
    }

    // 翌月の余白（グリッドを6行42個にする）
    const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const totalSlots = 42;
    const remainingSlots = totalSlots - days.length;

    for (let i = 1; i <= remainingSlots; i++) {
      days.push({
        date: new Date(nextMonthYear, nextMonth, i),
        isCurrentMonth: false,
      });
    }

    return days;
  }, [currentYear, currentMonth]);

  // 日付文字列の取得ヘルパー ("YYYY-MM-DD")
  const formatDateKey = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // 特定の日付に割り当てられたイベントや営業状態を取得
  const getDayInfo = useCallback(
    (date: Date) => {
      const dateStr = formatDateKey(date);
      const dayOfWeek = date.getDay(); // 0:日, 1:月, 2:火, ...
      const month = date.getMonth(); // 0-indexed

      // 1. Google カレンダーのイベントから一致する日付を検索
      const dayEvents = events
        .filter((e) => {
          if (e.start?.date) {
            const startStr = e.start.date;
            const endStr = e.end?.date || startStr;
            return dateStr >= startStr && dateStr < endStr;
          }
          if (e.start?.dateTime) {
            const startD = new Date(e.start.dateTime);
            const startStr = formatDateKey(startD);
            const endD = e.end?.dateTime ? new Date(e.end.dateTime) : startD;
            const endStr = formatDateKey(endD);
            return dateStr >= startStr && dateStr <= endStr;
          }
          return false;
        })
        .sort((a, b) => (a.summary || '').localeCompare(b.summary || '', 'ja'));

      const primaryEvent = dayEvents[0];

      // 2. 定休日判定ルール
      // 毎週 月曜日・火曜日は定休日。ただし、6月(5)は月火も営業。
      let isRegularClosed = false;
      if ((dayOfWeek === 1 || dayOfWeek === 2) && month !== 5) {
        isRegularClosed = true;
      }

      let config = EVENT_TYPE_CONFIGS.open;
      if (isRegularClosed) {
        config = EVENT_TYPE_CONFIGS.closed;
      }

      if (primaryEvent) {
        config = resolveEventConfig(
          primaryEvent.extendedProperties?.private?.type,
          primaryEvent.summary,
        );
      }

      const isClosed =
        (isRegularClosed && !primaryEvent) || !!config.isClosedDay;

      return {
        event: primaryEvent,
        dayEvents,
        config,
        isClosed,
        dayOfWeek,
      };
    },
    [events],
  );

  // 選択中の日付の営業詳細情報
  const selectedDayInfo = useMemo(() => {
    const info = getDayInfo(selectedDate);

    const formattedSelectedDate = `${selectedDate.getFullYear()}年${selectedDate.getMonth() + 1}月${selectedDate.getDate()}日(${WEEKDAYS[selectedDate.getDay()]})`;

    const config = info.config;
    const title = info.event?.summary || config.label;

    let timeLabel = info.event?.extendedProperties?.private?.timeLabel;
    if (
      !timeLabel &&
      info.event?.start?.dateTime &&
      info.event?.end?.dateTime
    ) {
      const s = new Date(info.event.start.dateTime);
      const e = new Date(info.event.end.dateTime);
      const pad = (n: number) => String(n).padStart(2, '0');
      timeLabel = `${pad(s.getHours())}:${pad(s.getMinutes())}〜${pad(e.getHours())}:${pad(e.getMinutes())}`;
    }

    return {
      formattedDate: formattedSelectedDate,
      title,
      config,
      description: info.event?.description,
      time: timeLabel || config.defaultTime,
      eventsList: info.dayEvents,
    };
  }, [selectedDate, getDayInfo]);

  return (
    <div className="min-h-screen">
      {/* ヘッダーセクション */}
      <section className="relative h-screen w-full flex items-end justify-start overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/schedule/header.jpg')" }}
        ></div>
        <div className={`absolute inset-0 ${heroColors.GRADIENT.HERO}`}></div>

        <div className="container mx-auto px-6 py-20 relative z-10">
          <div className="max-w-3xl">
            <h1
              className={`${FONTS.SERIF} ${heroColors.TEXT.DEFAULT} mb-8 leading-tight`}
              style={{ fontSize: 'clamp(2rem, 5vw, 3.75rem)' }}
            >
              スケジュール
            </h1>
            <p
              className={`${heroColors.TEXT.SEMI_MUTED} max-w-xl leading-relaxed`}
              style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)' }}
            >
              営業日・イベントのご案内
            </p>
          </div>
        </div>
      </section>

      {/* メインコンテンツセクション */}
      <section
        className={`py-16 md:py-24 ${calendarColors.BG.PRIMARY} relative overflow-hidden`}
      >
        {/* 背景テクスチャ */}
        <div className="absolute inset-0 bg-paper-texture opacity-10 pointer-events-none"></div>

        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* カレンダー本体 (左側: 7カラム) */}
            <div className="lg:col-span-7 bg-white p-6 md:p-8 rounded-none shadow-lg">
              {/* カレンダーヘッダー（月切り替え） */}
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={handlePrevMonth}
                  className="p-3 rounded-full hover:bg-natural-light/60 transition-colors text-primary-dark/80 hover:text-accent duration-200"
                  aria-label="前月"
                >
                  <FaChevronLeft className="w-4 h-4" />
                </button>

                <h3
                  className={`${FONTS.SERIF} text-2xl font-light text-primary-dark tracking-widest flex items-center gap-2`}
                >
                  <span>
                    {currentYear}年 {currentMonth + 1}月
                  </span>
                </h3>

                <button
                  onClick={handleNextMonth}
                  className="p-3 rounded-full hover:bg-natural-light/60 transition-colors text-primary-dark/80 hover:text-accent duration-200"
                  aria-label="翌月"
                >
                  <FaChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* 曜日ヘッダー */}
              <div className="grid grid-cols-7 gap-2 mb-4 text-center">
                {WEEKDAYS.map((day, idx) => {
                  let colorClass = 'text-primary-dark/60';
                  if (idx === 0) colorClass = 'text-red-500/80'; // 日曜
                  if (idx === 6) colorClass = 'text-blue-500/80'; // 土曜
                  return (
                    <div
                      key={`weekday-${idx}`}
                      className={`text-sm font-medium py-2 ${colorClass}`}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>

              {/* 日付グリッド */}
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((item, idx) => {
                  const info = getDayInfo(item.date);
                  const isSelected =
                    formatDateKey(item.date) === formatDateKey(selectedDate);
                  const IconComponent = info.config.icon;

                  // 日付マスの基本スタイル
                  let bgClass = item.isCurrentMonth
                    ? info.config.cellBg
                    : 'bg-transparent hover:bg-natural-light/40';
                  let textClass = item.isCurrentMonth
                    ? info.isClosed
                      ? 'text-gray-400 font-light'
                      : 'text-primary-dark'
                    : 'text-primary-dark/30';

                  // 日曜・土曜の色調整（当月かつ非定休日のみ）
                  if (item.isCurrentMonth && !info.isClosed) {
                    if (item.date.getDay() === 0) textClass = 'text-red-500';
                    if (item.date.getDay() === 6) textClass = 'text-blue-500';
                  }

                  // 選択中のスタイル
                  if (isSelected) {
                    bgClass = info.config.selectedBg;
                    textClass = 'text-white font-medium';
                  }

                  return (
                    <button
                      key={`day-${idx}`}
                      onClick={() => setSelectedDate(item.date)}
                      className={`relative aspect-square flex flex-col items-center justify-between p-2 rounded-none transition-all duration-300 ${bgClass} ${textClass}`}
                    >
                      {/* 日付数字 */}
                      <span className="text-sm md:text-base">
                        {item.date.getDate()}
                      </span>

                      {/* ステータスアイコン (複数予定はあいうえお順ですべて表示) */}
                      <div className="flex flex-wrap items-center justify-center gap-1 min-h-[16px] md:min-h-[20px] max-w-full px-0.5">
                        {item.isCurrentMonth &&
                          (info.dayEvents.length > 0 ? (
                            info.dayEvents.map((ev, evIdx) => {
                              const evConfig = resolveEventConfig(
                                ev.extendedProperties?.private?.type,
                                ev.summary,
                              );
                              const EvIcon = evConfig.icon;
                              return (
                                <EvIcon
                                  key={ev.id || evIdx}
                                  className={`w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0 ${
                                    isSelected
                                      ? 'text-white'
                                      : evConfig.iconColor
                                  }`}
                                  title={ev.summary || evConfig.label}
                                />
                              );
                            })
                          ) : info.isClosed ? (
                            <IconComponent
                              className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                                isSelected
                                  ? 'text-white'
                                  : info.config.iconColor
                              }`}
                              title={info.config.label}
                            />
                          ) : null)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* スケジュール詳細 (右側: 5カラム) */}
            <div className="lg:col-span-5">
              <AnimatePresence mode="wait">
                <motion.div
                  key={formatDateKey(selectedDate)}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="bg-white p-6 md:p-8 rounded-none shadow-lg"
                >
                  <div className="pb-4 mb-6">
                    <span className="text-xs font-semibold tracking-wider text-accent2 uppercase block mb-1">
                      Schedule Details
                    </span>
                    <h3
                      className={`${FONTS.SERIF} text-xl md:text-2xl text-primary-dark font-medium`}
                    >
                      {selectedDayInfo.formattedDate}
                    </h3>
                  </div>

                  {/* 状態に応じたアイコニックな表示 */}
                  <div className="space-y-6">
                    {selectedDayInfo.eventsList.length > 0 ? (
                      selectedDayInfo.eventsList.map((ev, evIdx) => {
                        const evConfig = resolveEventConfig(
                          ev.extendedProperties?.private?.type,
                          ev.summary,
                        );
                        let evTimeLabel =
                          ev.extendedProperties?.private?.timeLabel;
                        if (
                          !evTimeLabel &&
                          ev.start?.dateTime &&
                          ev.end?.dateTime
                        ) {
                          const s = new Date(ev.start.dateTime);
                          const e = new Date(ev.end.dateTime);
                          const pad = (n: number) => String(n).padStart(2, '0');
                          evTimeLabel = `${pad(s.getHours())}:${pad(s.getMinutes())}〜${pad(e.getHours())}:${pad(e.getMinutes())}`;
                        }
                        const evTime = evTimeLabel || evConfig.defaultTime;

                        return (
                          <div
                            key={ev.id || evIdx}
                            className={`flex items-start gap-4 ${
                              evIdx > 0 ? 'pt-6' : ''
                            }`}
                          >
                            <div
                              className={`p-3 rounded-none ${evConfig.badgeBg} ${evConfig.badgeTextColor}`}
                            >
                              <evConfig.icon className="w-6 h-6" />
                            </div>

                            <div className="flex-grow">
                              <h4 className="text-lg font-medium text-primary-dark mb-1">
                                {ev.summary || evConfig.label}
                              </h4>

                              {evTime && (
                                <div className="flex items-center gap-1.5 text-xs text-primary-dark/60 mb-3">
                                  <FaClock />
                                  <span>{evTime}</span>
                                </div>
                              )}

                              {ev.description && (
                                <p className="text-xs text-primary-dark/70 leading-relaxed whitespace-pre-wrap mt-2 bg-natural-light/30 p-2.5">
                                  {ev.description}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex items-start gap-4">
                        <div
                          className={`p-3 rounded-none ${selectedDayInfo.config.badgeBg} ${selectedDayInfo.config.badgeTextColor}`}
                        >
                          <selectedDayInfo.config.icon className="w-6 h-6" />
                        </div>

                        <div className="flex-grow">
                          <h4 className="text-lg font-medium text-primary-dark mb-1">
                            {selectedDayInfo.title}
                          </h4>

                          {selectedDayInfo.time && (
                            <div className="flex items-center gap-1.5 text-xs text-primary-dark/60 mb-3">
                              <FaClock />
                              <span>{selectedDayInfo.time}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CalendarPage;
